import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api, apiError, API } from "../lib/api";
import Layout from "../components/Layout";
import ResourceContent from "../components/ResourceContent";
import ImagePickerModal from "../components/ImagePickerModal";
import { toast } from "sonner";
import { Loader2, Download, Wand2, FileText, RefreshCw, Upload, Lock, Sparkles, X, PenLine } from "lucide-react";

const CONFIG = {
  presentation: { title: "Генератор на презентации", endpoint: "/generate/presentation", icon: "🎨", placeholder: "напр. Слънчевата система, Второто българско царство...", showSlides: true },
  worksheet: { title: "Генератор на работни листове", endpoint: "/generate/worksheet", icon: "📝", placeholder: "напр. Събиране и изваждане до 100...", showQuestions: true },
  "lesson-plan": { title: "Резюме на урока", endpoint: "/generate/lesson-plan", icon: "📄", placeholder: "напр. Частите на речта, Кръговрат на водата..." },
};

const GRADES = ["1. клас","2. клас","3. клас","4. клас","5. клас","6. клас","7. клас","8. клас","9. клас","10. клас","11. клас","12. клас"];
const SUBJECTS = ["Български език и литература","Математика","Околен свят","Човекът и природата","Човекът и обществото","История и цивилизации","География и икономика","Биология и здравно образование","Физика и астрономия","Химия и опазване на околната среда","Информационни технологии","Изобразително изкуство","Музика","Физическо възпитание и спорт","Технологии и предприемачество","Английски език"];
const TEXTBOOKS = ["","Просвета","Просвета Плюс","Просвета АзБуки","Булвест 2000","Клет България","Анубис","Педагог 6","Рива","Даниела Убенова","Изкуства","БГ Учебник","Архимед","Регалия 6"];
const MODELS = [
  { v: "gpt-5.4", l: "GPT-5.4", d: "Балансиран и надежден. Най-добър за ежедневни задачи и бързо генериране." },
  { v: "claude-sonnet-4-6", l: "Claude Sonnet 4.6", d: "Отличен за дълги, добре структурирани текстове и прецизни инструкции." },
  { v: "gemini-3.1-pro", l: "Gemini 3.1 Pro", d: "Силен при сложни теми, задълбочен анализ и творчески идеи." },
];
// Presentation-wide design themes (PowerPoint-style) — must match backend THEMES ids
const THEMES = [
  { id: "classic", name: "Класическо", bg: "#FFFFFF", accent: "#0CA5E9", title: "#0F172A", font: "Georgia" },
  { id: "navy", name: "Тъмно синьо", bg: "#0F2A4A", accent: "#FBBF24", title: "#FFFFFF", font: "Trebuchet MS" },
  { id: "forest", name: "Горско зелено", bg: "#0E3B2E", accent: "#A3E635", title: "#FFFFFF", font: "Cambria" },
  { id: "cream", name: "Топло крем", bg: "#FFF7ED", accent: "#EA580C", title: "#7C2D12", font: "Georgia" },
  { id: "slate", name: "Минимал сиво", bg: "#F1F5F9", accent: "#0D9488", title: "#1E293B", font: "Century Gothic" },
  { id: "purple", name: "Лилава нощ", bg: "#2A1A4A", accent: "#F472B6", title: "#FFFFFF", font: "Trebuchet MS" },
  { id: "ocean", name: "Океан", bg: "#083344", accent: "#22D3EE", title: "#FFFFFF", font: "Cambria" },
  { id: "pastel", name: "Пастел", bg: "#FDF2F8", accent: "#EC4899", title: "#831843", font: "Georgia" },
];
// Subject-specific "additional instructions" presets (options change per subject)
const SUBJECT_OPTIONS = {
  "Математика": ["Включи текстови (словесни) задачи","Добави геометрични задачи","Покажи решения стъпка по стъпка","Задачи с практическо приложение","Задачи за логическо мислене"],
  "Български език и литература": ["Задачи за правопис и пунктуация","Анализ на кратък текст","Упражнения за части на речта","Съчинение по картина/тема","Речников запас и синоними"],
  "История и цивилизации": ["Хронология на събития","Работа с исторически източник","Причини и последици","Важни личности и роли","Карта и историческа география"],
  "Биология и здравно образование": ["Схеми/диаграми за попълване","Класификация на организми","Опити и наблюдения","Терминологичен речник","Връзка с ежедневието и здравето"],
  "Физика и астрономия": ["Задачи с формули и единици","Обяснение на явление","Практически опит","Графики и таблици","Реални примери от живота"],
  "Химия и опазване на околната среда": ["Химични реакции и уравнения","Безопасност в лабораторията","Свойства на вещества","Екологичен аспект","Изчислителни задачи"],
  "География и икономика": ["Работа с карта","Диаграми и статистика","Природни зони/райони","Икономически понятия","Сравнение на региони"],
  "Английски език": ["Речник (vocabulary) упражнения","Граматика в контекст","Четене с разбиране","Диалози и ситуации","Превод на изрази"],
};
const GENERIC_OPTIONS = ["Направи го по-лесно","Направи го по-предизвикателно","Добави забавни елементи","Свържи с ежедневието","Добави визуални описания"];

export default function Generate({ type }) {
  const cfg = CONFIG[type];
  const [form, setForm] = useState({ topic: "", topics: [], grade: "5. клас", subject: SUBJECTS[1], textbook: "", model: "gpt-5.4", slides: 8, num_questions: 5, difficulty: "средно", extra: "", theme: "classic", theme_color: "#0CA5E9", bg_image: "", transition: "fade", plan_format: "detailed", duration: "40 минути", objectives: "", materials: "" });
  const [models, setModels] = useState([]);
  const [editInstr, setEditInstr] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data)).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [imgDialog, setImgDialog] = useState({ open: false, slideIdx: null });
  const [dirty, setDirty] = useState(false);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [custom, setCustom] = useState(false);

  const fetchThemes = async () => {
    setThemesLoading(true);
    setThemes([]);
    try {
      const { data } = await api.post("/themes", { subject: form.subject, grade: form.grade, textbook: form.textbook, model: form.model });
      setThemes(data.themes || []);
      if (!(data.themes || []).length) toast.error("Не успяхме да заредим теми. Използвайте ръчно въвеждане.");
    } catch {
      setThemes([]);
      toast.error("Грешка при зареждане на темите. Опитайте 'Обнови темите'.");
    } finally {
      setThemesLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
    setForm((f) => ({ ...f, topic: "", topics: [] }));
    setCustom(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subject, form.grade, form.textbook]);

  const selectTheme = (t) => setForm((f) => ({ ...f, theme: t.id, theme_color: t.accent }));
  const addTopic = (val) => {
    const v = (val || "").trim();
    if (!v || v === "__custom__") return;
    setForm((f) => (f.topics.includes(v) ? f : { ...f, topics: [...f.topics, v] }));
  };
  const removeTopic = (v) => setForm((f) => ({ ...f, topics: f.topics.filter((x) => x !== v) }));
  const addPreset = (val) => {
    if (!val) return;
    setForm((f) => ({ ...f, extra: f.extra ? `${f.extra}; ${val}` : val }));
  };
  const onBgUpload = (file) => {
    if (!file) return setForm((f) => ({ ...f, bg_image: "" }));
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxW = 1280;
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setForm((f) => ({ ...f, bg_image: canvas.toDataURL("image/jpeg", 0.82) }));
      };
      img.onerror = () => setForm((f) => ({ ...f, bg_image: reader.result }));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const errMsg = (err) => (err?.response ? apiError(err.response?.data?.detail) : (err?.message || "Възникна грешка. Опитайте отново."));

  const pollJob = async (jobId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      const { data } = await api.get(`/generate/status/${jobId}`);
      if (data.status === "done") return data.resource;
      if (data.status === "error") throw new Error(data.error || "Възникна грешка при генерирането.");
    }
    throw new Error("Генерирането отнема твърде дълго. Опитайте с по-малко слайдове/въпроси.");
  };

  const generate = async () => {
    let topics = form.topics;
    if (type === "worksheet" && form.topic.trim() && !topics.includes(form.topic.trim())) topics = [...topics, form.topic.trim()];
    const hasTopic = type === "worksheet" ? topics.length > 0 : form.topic.trim();
    if (!hasTopic) return toast.error(type === "worksheet" ? "Добавете поне една тема" : "Изберете тема");
    setLoading(true); setResult(null); setDirty(false);
    try {
      const payload = { ...form, topics };
      if (type === "worksheet" && topics.length) payload.topic = topics[0];
      const { data } = await api.post(cfg.endpoint, payload);
      const resource = await pollJob(data.job_id);
      setResult(resource);
      toast.success("Готово! Материалът е запазен.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  const applyEdit = async () => {
    if (!editInstr.trim()) return toast.error("Опишете каква промяна искате");
    setEditing(true);
    try {
      const { data } = await api.post(`/resources/${result.id}/edit-ai`, { instruction: editInstr, model: form.model });
      const resource = await pollJob(data.job_id);
      setResult(resource);
      setEditInstr("");
      toast.success("Промяната е приложена");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setEditing(false);
    }
  };

  const pickImage = (url) => {
    setResult((r) => {
      const slides = [...r.content.slides];
      slides[imgDialog.slideIdx] = { ...slides[imgDialog.slideIdx], image_url: url };
      return { ...r, content: { ...r.content, slides } };
    });
    setDirty(true);
  };

  const saveImages = async () => {
    try {
      await api.patch(`/resources/${result.id}`, { content: result.content });
      setDirty(false);
      toast.success("Изображенията са запазени");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="max-w-5xl">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900">{cfg.icon} {cfg.title}</h1>
        <p className="text-slate-500 mt-2">Опиши какво ти трябва и остави останалото на AI.</p>

        <div className="bg-white border border-slate-100 rounded-3xl p-7 mt-6 shadow-sm space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Учебен предмет</label>
              <select data-testid="subject-select" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Клас</label>
              <select data-testid="grade-select" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {GRADES.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Учебник (издателство)</label>
              <select data-testid="textbook-select" value={form.textbook} onChange={(e) => setForm({ ...form, textbook: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {TEXTBOOKS.map((t) => <option key={t} value={t}>{t || "Не е посочен"}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-bold text-slate-700">{type === "worksheet" ? "Теми (може повече от една)" : "Тема (по учебната програма)"}</label>
              <button type="button" data-testid="refresh-themes-btn" onClick={fetchThemes} disabled={themesLoading}
                className="text-xs font-bold text-sky-600 flex items-center gap-1 hover:underline disabled:opacity-50">
                <RefreshCw size={13} className={themesLoading ? "animate-spin" : ""} /> Обнови темите
              </button>
            </div>
            {type === "worksheet" ? (
              <>
                {!custom ? (
                  <select data-testid="topic-multi-select" value="" disabled={themesLoading}
                    onChange={(e) => { if (e.target.value === "__custom__") { setCustom(true); } else { addTopic(e.target.value); } }}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white disabled:opacity-60">
                    <option value="">{themesLoading ? "Зареждане на теми..." : "— Добави тема —"}</option>
                    {themes.filter((t) => !form.topics.includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                    <option value="__custom__">✏️ Друга тема (ръчно въвеждане)</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input data-testid="topic-custom-input" value={form.topic} placeholder={cfg.placeholder}
                      onChange={(e) => setForm({ ...form, topic: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(form.topic); setForm((f) => ({ ...f, topic: "" })); } }}
                      className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none" />
                    <button type="button" data-testid="add-topic-btn" onClick={() => { addTopic(form.topic); setForm((f) => ({ ...f, topic: "" })); }}
                      className="px-4 rounded-xl bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition-colors">Добави</button>
                    <button type="button" data-testid="back-to-list-btn" onClick={() => { setCustom(false); setForm({ ...form, topic: "" }); }}
                      className="px-4 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200 transition-colors">Списък</button>
                  </div>
                )}
                {form.topics.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="selected-topics">
                    {form.topics.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-bold">
                        {t}
                        <button type="button" data-testid={`remove-topic-${t}`} onClick={() => removeTopic(t)}
                          className="hover:bg-sky-200 rounded-full p-0.5 transition-colors"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : !custom ? (
              <select data-testid="topic-select" value={form.topic} disabled={themesLoading}
                onChange={(e) => { if (e.target.value === "__custom__") { setCustom(true); setForm({ ...form, topic: "" }); } else setForm({ ...form, topic: e.target.value }); }}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white disabled:opacity-60">
                <option value="">{themesLoading ? "Зареждане на теми..." : "— Изберете тема —"}</option>
                {themes.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__custom__">✏️ Друга тема (ръчно въвеждане)</option>
              </select>
            ) : (
              <div className="flex gap-2">
                <input data-testid="topic-input" value={form.topic} placeholder={cfg.placeholder}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none" />
                <button type="button" data-testid="back-to-list-btn" onClick={() => { setCustom(false); setForm({ ...form, topic: "" }); }}
                  className="px-4 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200 transition-colors">Списък</button>
              </div>
            )}
          </div>

          {type === "presentation" && (
            <div>
              <label className="text-sm font-bold text-slate-700 mb-2 block">Дизайн на презентацията (за всички слайдове)</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="theme-grid">
                {THEMES.map((t) => (
                  <button type="button" key={t.id} data-testid={`theme-${t.id}`} onClick={() => selectTheme(t)}
                    className={`rounded-2xl border-2 overflow-hidden text-left transition-all ${form.theme === t.id ? "border-slate-900 ring-2 ring-slate-900/10 shadow-md" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="h-16 p-2.5 flex flex-col justify-between" style={{ backgroundColor: t.bg }}>
                      <span className="text-[11px] font-bold truncate" style={{ color: t.title, fontFamily: t.font }}>Заглавие на слайд</span>
                      <span className="h-1.5 w-8 rounded-full" style={{ backgroundColor: t.accent }} />
                    </div>
                    <div className="px-2.5 py-1.5 bg-white">
                      <span className="text-xs font-bold text-slate-700">{t.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {type === "presentation" && (
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Собствен дизайн за заглавен слайд (по избор)</label>
              {form.bg_image ? (
                <div className="flex items-center gap-3">
                  <img src={form.bg_image} alt="" className="h-16 rounded-lg object-cover border border-slate-200" />
                  <button type="button" data-testid="remove-bg-btn" onClick={() => onBgUpload(null)} className="text-xs font-bold text-red-500 hover:underline">Премахни</button>
                </div>
              ) : (
                <label data-testid="upload-bg-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-sky-300 hover:bg-sky-50/50 text-sm font-medium text-slate-500 transition-colors">
                  <Upload size={16} className="text-sky-400" /> Качи собствено изображение за фон
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onBgUpload(e.target.files?.[0])} />
                </label>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {cfg.showSlides && (
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1.5 block">Брой слайдове</label>
                <input data-testid="slides-input" type="number" min="3" max="20" value={form.slides}
                  onChange={(e) => setForm({ ...form, slides: parseInt(e.target.value) || 8 })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none" />
              </div>
            )}
            {cfg.showQuestions && (
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1.5 block">Брой въпроси</label>
                <input data-testid="questions-input" type="number" min="1" max="30" value={form.num_questions}
                  onChange={(e) => setForm({ ...form, num_questions: Math.min(30, parseInt(e.target.value) || 5) })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none" />
              </div>
            )}
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">AI модел</label>
              <select data-testid="model-select" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                {(models.length ? models : MODELS.map((m) => ({ id: m.v, label: m.l, desc: m.d, locked: false }))).map((m) => (
                  <option key={m.id} value={m.id} disabled={m.locked}>{m.label}{m.locked ? " 🔒 (само Pro)" : ""}</option>
                ))}
              </select>
              {(() => {
                const list = models.length ? models : MODELS.map((m) => ({ id: m.v, label: m.l, desc: m.d, locked: false }));
                const sel = list.find((m) => m.id === form.model);
                return sel ? (
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed flex items-start gap-1" data-testid="model-desc">
                    {sel.locked && <Lock size={12} className="text-amber-500 mt-0.5 shrink-0" />} {sel.desc}
                  </p>
                ) : null;
              })()}
            </div>
            {cfg.showSlides && (
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1.5 block">Преход между слайдовете</label>
                <select data-testid="transition-select" value={form.transition} onChange={(e) => setForm({ ...form, transition: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none bg-white">
                  <option value="none">Без преход</option>
                  <option value="fade">Избледняване</option>
                  <option value="push">Избутване</option>
                  <option value="wipe">Забърсване</option>
                  <option value="split">Разделяне</option>
                  <option value="cover">Покриване</option>
                  <option value="cut">Рязане</option>
                </select>
              </div>
            )}
          </div>

          {type === "lesson-plan" && (
            <div>
              <label className="text-sm font-bold text-slate-700 mb-1.5 block">Формат на плана</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "detailed", t: "Подробен / Нормален", d: "С обяснения, методология, действия на учителя, идеи и насоки." },
                  { v: "structured", t: "Структуриран / Кратък", d: "Ясни секции, стъпки и подточки — лесен за следене по време на урока." },
                ].map((o) => (
                  <button type="button" key={o.v} data-testid={`format-${o.v}`} onClick={() => setForm({ ...form, plan_format: o.v })}
                    className={`text-left p-4 rounded-2xl border-2 transition-colors ${form.plan_format === o.v ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className="font-heading font-bold text-slate-900 text-sm">{o.t}</div>
                    <div className="text-xs text-slate-400 mt-1 leading-relaxed">{o.d}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
              <label className="text-sm font-bold text-slate-700">Допълнителни указания (по избор)</label>
              <select data-testid="instr-preset-select" value=""
                onChange={(e) => addPreset(e.target.value)}
                className="text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2 py-1.5 outline-none cursor-pointer hover:bg-sky-100 transition-colors">
                <option value="">+ Готови предложения</option>
                {(SUBJECT_OPTIONS[form.subject] || GENERIC_OPTIONS).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <textarea data-testid="extra-input" rows="2" value={form.extra} placeholder="напр. добави викторина накрая, направи го цветно и забавно..."
              onChange={(e) => setForm({ ...form, extra: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none resize-none" />
          </div>
          <button data-testid="generate-btn" onClick={generate} disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 size={20} className="animate-spin" /> AI създава...</> : <><Wand2 size={20} /> Генерирай</>}
          </button>
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-heading text-2xl font-bold text-slate-900">{result.title}</h2>
              <div className="flex items-center gap-2">
                {type === "presentation" && dirty && (
                  <button data-testid="save-images-btn" onClick={saveImages}
                    className="bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">Запази промените</button>
                )}
                {type === "presentation" && (
                  <a data-testid="download-pptx-btn" href={`${API}/resources/${result.id}/pptx`}
                    className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                    <Download size={16} /> .pptx
                  </a>
                )}
                {type !== "presentation" && (
                  <>
                    <a data-testid="download-pdf-btn" href={`${API}/resources/${result.id}/pdf`}
                      className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                      <FileText size={16} /> Изтегли PDF
                    </a>
                    <a data-testid="download-docx-btn" href={`${API}/resources/${result.id}/docx`}
                      className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors">
                      <PenLine size={16} /> Редактирай в Word
                    </a>
                  </>
                )}
              </div>
            </div>
            <ResourceContent resource={result} onEditImage={type === "presentation" ? (i) => setImgDialog({ open: true, slideIdx: i }) : undefined} />

            <div className="mt-6 bg-amber-50/50 border-2 border-amber-200 rounded-3xl p-6 shadow-sm" data-testid="ai-edit-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-amber-500" />
                <h3 className="font-heading font-bold text-slate-900">Не си доволен? Редактирай с AI</h3>
              </div>
              <p className="text-xs text-slate-400 mb-3">Опиши промяна, подобрение или корекция и AI ще я приложи директно ({(models.find((m) => m.id === form.model) || {}).label || form.model}).</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input data-testid="ai-edit-input" value={editInstr} onChange={(e) => setEditInstr(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !editing && applyEdit()}
                  placeholder="напр. направи текста по-кратък, добави пример, поправи 3-ти слайд..."
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-300 outline-none" />
                <button data-testid="ai-edit-btn" onClick={applyEdit} disabled={editing}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-3 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
                  {editing ? <><Loader2 size={18} className="animate-spin" /> Прилагам...</> : <><Wand2 size={18} /> Приложи с AI</>}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      <ImagePickerModal open={imgDialog.open} onOpenChange={(o) => setImgDialog({ ...imgDialog, open: o })} onSelect={pickImage} />
    </Layout>
  );
}
